/* eslint react-hooks/rules-of-hooks: "warn" */
import React, { useMemo, useState } from 'react'
import { css } from '@emotion/react'
import moment from 'moment'
import {
  colors,
  fontSize,
  type GridColumns,
  Modal,
  spacing,
  Text,
} from '@retailer-platform/shared-components'
import { SVGIcon } from 'ic-snacks'
import { TextButton } from '@retailer-platform/shared-components'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { useDashMessages } from 'intl/intl.hooks'
import { dateFormat } from 'domains/merchandising/constants/heroBanner.constants'
import { bannerTypes } from 'domains/banners/constants/banner.constants'

export interface BannersListDatum {
  name: string
  preview: React.Component
  platform: ReadonlyArray<string>
  startDate: string
  endDate: string
  status: string
  enabled: boolean
}

const statusHeaderStyles = css({
  display: 'flex',
  '& > svg': {
    marginRight: spacing.X4,
  },
})

const statusTitleStyles = css({
  fontSize: fontSize.X15,
  marginRight: 0,
  textTransform: 'uppercase',
})

export const useBannersListColumns = routingConfig => {
  const messages = useDashMessages({
    date: 'bannersDomain.list.columns.date.header',
    name: 'bannersDomain.list.columns.name.header',
    platform: 'bannersDomain.list.columns.platform.header',
    type: 'bannersDomain.list.columns.name.type',
    enabled: 'bannersDomain.list.columns.enabled.header',
    status: 'bannersDomain.list.columns.status.header',
    emptyValue: 'bannersDomain.field.empty.value',
    enabledStatus: 'bannersDomain.list.enabled.true',
    disabledStatus: 'bannersDomain.list.enabled.false',

    liveDefinition: 'merchandising.hero-banners.status.live.definition',
    liveTitle: 'merchandising.hero-banners.status.live',
    activeDefinition: 'merchandising.hero-banners.status.active.definition',
    activeTitle: 'merchandising.hero-banners.status.active',
    inactiveDefinition: 'merchandising.hero-banners.status.inactive.definition',
    inactiveTitle: 'merchandising.hero-banners.status.inactive',
  })

  const [statusTooltipVisible, setStatusTooltipVisible] = useState(false)

  const columns: GridColumns<BannersListDatum> = useMemo(() => {
    const columnHeaders = [
      {
        Cell: ({ row }) => {
          if (row?.original?.name) {
            const goToDetailsPage = useGoToPath(routingConfig.details, {
              placementId: row.original.id,
            })

            return <TextButton onClick={goToDetailsPage}>{row.original.name}</TextButton>
          } else {
            return <div />
          }
        },
        width: 320,
        Header: messages.name,
        id: 'name',
      },
      {
        Cell: ({ row }) => row?.original?.platform?.join(', '),
        width: 200,
        Header: messages.platform,
        id: 'platform',
      },
      {
        Cell: ({ row }) => row?.original?.type,
        with: 180,
        Header: messages.type,
        id: 'bannerType',
      },
      {
        Cell: ({ row }) => {
          if (row?.original?.scheduling) {
            const { startDate, endDate } = row.original.scheduling
            const start = startDate ? moment(startDate).format(dateFormat) : messages.emptyValue
            const end = endDate ? moment(endDate).format(dateFormat) : messages.emptyValue

            return `${start} - ${end}`
          }
        },
        with: 200,
        Header: messages.date,
        id: 'date',
      },
      {
        Cell: ({ row }) => row?.original?.status,
        width: 70,
        Header: (
          <>
            <div css={statusHeaderStyles} onClick={() => setStatusTooltipVisible(true)}>
              <SVGIcon name="infoFilled" size="small" />
              {messages.status}
            </div>
            <Modal
              isOpen={statusTooltipVisible}
              headerContent={
                <Text size="large" weight="bold">
                  {messages.status}
                </Text>
              }
              maxWidth={640}
              bodyContent={
                <>
                  <p>
                    <Text display="inline" weight="bold" styles={statusTitleStyles}>
                      {messages.activeTitle + ' - '}
                    </Text>
                    {messages.activeDefinition}
                  </p>
                  <p>
                    <Text display="inline" weight="bold" styles={statusTitleStyles}>
                      {messages.inactiveTitle + ' - '}
                    </Text>
                    {messages.inactiveDefinition}
                  </p>
                </>
              }
              onRequestClose={() => setStatusTooltipVisible(!statusTooltipVisible)}
            />
          </>
        ),
        id: 'status',
      },
      {
        Cell: ({ row }) => {
          if (row?.original?.enabled) {
            return messages.enabledStatus.toUpperCase()
          } else {
            return messages.disabledStatus.toUpperCase()
          }
        },
        with: 100,
        Header: messages.enabled,
        id: 'enabled',
      },
      {
        Header: () => null,
        width: 30,
        id: 'edit',
        Cell: ({ row }) => {
          if (row?.original?.id) {
            const goToEditPage = useGoToPath(routingConfig.edit, {
              placementId: row.original.id,
            })

            if (
              row?.original?.type == bannerTypes.IMAGE_FORWARD ||
              row?.original?.type == bannerTypes.TEXT_WITH_THUMBNAIL ||
              row?.original?.type == bannerTypes.TEXT_WITH_THUMBNAIL_FLEX ||
              row?.original?.type == bannerTypes.IMAGE_FORWARD_FLEX ||
              row?.original?.type == bannerTypes.HERO_BANNER ||
              row?.original?.type == bannerTypes.SECONDARY_BANNER ||
              row?.original?.type == bannerTypes.FLYOUT
            ) {
              return (
                <div>
                  <TextButton onClick={goToEditPage}>
                    <SVGIcon name="pencil" color={colors.PRIMARY.REGULAR} />
                  </TextButton>
                </div>
              )
            } else {
              return <Text></Text>
            }
          } else {
            return <div />
          }
        },
      },
    ]

    return columnHeaders
  }, [messages, statusTooltipVisible, routingConfig])

  return columns
}
